import { ArrowRight } from "lucide-react";
import { FindingCardProps, RouteWithStats } from "@/types/admin/page-views";
import {
  CAUTION_CATEGORY_LABELS,
  CONFIDENCE_STYLES,
  formatDay,
  formatNumber,
  ROUTE_KIND_CHIP_STYLES,
  ROUTE_KIND_LABELS,
} from "./constants";

const Metric = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <div className="min-w-0 rounded-xl bg-[#F9FAFC] px-3 py-2">
    <div className="text-[11px] font-medium uppercase tracking-wide text-[#64748B]">{label}</div>
    <div className="truncate text-sm font-semibold tabular-nums text-[#222B45]">{value}</div>
    {hint && <div className="truncate text-[11px] text-gray-500">{hint}</div>}
  </div>
);

export const RouteMetrics = ({ route }: { route: RouteWithStats }) => {
  const { stats } = route;
  const trend =
    stats.previousViews === null
      ? "prior period not tracked"
      : `${formatNumber(stats.previousViews)} in prior period`;

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
      <Metric label="Views" value={formatNumber(stats.views)} hint={trend} />
      <Metric label="URLs seen" value={formatNumber(stats.instances)} />
      <Metric label="Active days" value={formatNumber(stats.activeDays)} />
      <Metric label="Last visit" value={formatDay(stats.lastSeen)} />
      <Metric label="In-app links" value={formatNumber(route.references)} />
      <Metric label="In nav" value={route.inNav ? "Yes" : "No"} />
    </div>
  );
};

const FindingCard = (props: FindingCardProps) => {
  const { finding } = props;
  const { route } = finding;
  const badge =
    props.variant === "candidate"
      ? CONFIDENCE_STYLES[props.finding.confidence]
      : { label: CAUTION_CATEGORY_LABELS[props.finding.category] ?? "Keep", className: "bg-genie-primary/10 text-genie-primary" };

  return (
    <article className="rounded-2xl border border-[#E5EAF0] bg-white p-4 shadow-sm transition-all hover:border-genie-primary/40 hover:shadow-md md:p-5">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="break-all font-mono text-[15px] font-semibold text-[#222B45]">{route.path}</h3>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${ROUTE_KIND_CHIP_STYLES[route.kind]}`}>
              {ROUTE_KIND_LABELS[route.kind]}
            </span>
          </div>
          {route.component && (
            <p className="break-all text-xs text-[#64748B]">
              {route.component}
              {route.file && <span className="text-gray-400"> · {route.file}</span>}
            </p>
          )}
        </div>
        <span className={`w-fit shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${badge.className}`}>
          {badge.label}
        </span>
      </header>

      <div className="mt-4">
        <RouteMetrics route={route} />
      </div>

      <p className="mt-4 text-sm leading-relaxed text-gray-700">{finding.reasoning}</p>

      {finding.signals.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {finding.signals.map((signal) => (
            <li key={signal} className="flex gap-2 text-sm text-gray-600">
              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" />
              <span>{signal}</span>
            </li>
          ))}
        </ul>
      )}

      {props.variant === "candidate" && props.finding.recommendation && (
        <div className="mt-4 flex gap-2.5 rounded-xl border border-genie-primary/20 bg-genie-primary/5 px-3.5 py-2.5 text-sm text-[#222B45]">
          <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-genie-primary" />
          <span>
            <span className="font-semibold">Next step: </span>
            {props.finding.recommendation}
          </span>
        </div>
      )}
    </article>
  );
};

export default FindingCard;
